<template>
    <div class="py-12">
        <h1
            class="text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            {{ t("vue.admin_users") }}
        </h1>
        <div class="mt-10 flex flex-col sm:flex-row">
            <TextInput
                v-model="form.email"
                class="w-52 sm:mr-6 sm:w-80"
                :label="t('vue.new_admin')"
                :placeholder="t('vue.provide_valid_email_address')"
            />
            <div class="mt-4 sm:mt-[27px]">
                <AtomicButton
                    class=""
                    color="yellow"
                    size="sm"
                    :disabled="!form.email"
                    @click="handlePromoteUser"
                    >{{ t("vue.promote_user") }}</AtomicButton
                >
            </div>
        </div>
        <DataTable class="mt-10" :headers="headers" :items="admins">
            <template #item-profile_photo_url="{ data, row }">
                <div class="flex justify-center">
                    <div
                        class="h-10 w-10 cursor-pointer rounded-full ring-[1px] ring-gray-300"
                    >
                        <img
                            :src="data ? data : row.profile_photo_url"
                            class="h-10 w-10 rounded-full object-cover"
                            alt="Admin kiosk profile photo"
                        />
                    </div>
                </div>
            </template>

            <template #item-custom="{ row }">
                <AtomicButton
                    color="red"
                    size="sm"
                    @click="handleDemoteAdmin(row)"
                >
                    <template #icon>
                        <UserMinusIcon class="h-6 w-6" />
                    </template>
                </AtomicButton>
            </template>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import { UserMinusIcon } from "@heroicons/vue/24/outline";
import type IUser from "@/types/Members/IUser";
import route from "ziggy-js";
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";
import { useForm } from "@inertiajs/vue3";
import useConfirm from "@/composables/useConfirm";
import { router } from "@inertiajs/vue3";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";

type AdminForm = {
    email: string;
};

defineProps<{
    admins: IPaginationData<IUser>;
}>();

// Get access to translations
const { t } = useTranslations();

const headers: IDataTableHeader[] = [
    { text: "", id: "profile_photo_url" },
    {
        text: t("vue.name"),
        id: "name",
        sortable: true,
        sortableId: "name",
    },
    {
        text: t("vue.email"),
        id: "email",
        sortable: true,
        sortableId: "email",
    },
    { text: "", id: "custom" },
];

const { show } = useConfirm();

const form = useForm<AdminForm>({
    email: "",
});

const handleDemoteAdmin = async (user: IUser) => {
    const confirmed = await show({
        header: `${t("vue.demote_admin_header")} ${user.name}`,
        message: `${t("vue.demote_admin_message")} ${user.name}?`,
        type: "danger",
        confirm: t("vue.demote"),
    });
    if (confirmed) {
        demoteAdmin(user);
    }
};

const demoteAdmin = (user: IUser) => {
    router.delete(
        route("kiosk.admins.delete", {
            user: user.id,
        }),
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: t("vue.admin_demoted"),
                    type: "success",
                });
            },
            onError: () => {
                create({
                    title: t("vue.admin_not_demoted"),
                    type: "error",
                });
            },
        },
    );
};

const promoteUser = () => {
    form.post(route("kiosk.admins.store"), {
        preserveScroll: true,
        onSuccess: () => {
            create({
                title: t("vue.user_promoted"),
                type: "success",
            });
            form.email = "";
        },
        onError: () => {
            create({
                title: t("vue.user_not_promoted"),
                type: "error",
            });
        },
    });
};

const handlePromoteUser = async () => {
    const confirmed = await show({
        header: t("vue.promote_user_header"),
        message: `${t("vue.promote_user_message")} ${form.email}?`,
        type: "info",
        confirm: t("vue.promote_user"),
    });
    if (confirmed) {
        promoteUser();
    }
};
</script>

<style></style>
